import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { ReactComponent as ListCheckmark } from 'assets/icons/list-checkmark.svg';

type MarkdownContentProps = {
  content?: string | null;
  variant?: 'default' | 'intro' | (string & {});
  componentsOverride?: any;
  className?: string;
};

const baseComponents: any = {
  p: ({ children }: any) => <p className="tw-mb-6 tw-leading-6">{children}</p>,
  h1: ({ children }: any) => <h1 className="tw-mb-6 tw-text-2xl tw-font-bold tw-text-white">{children}</h1>,
  h2: ({ children }: any) => <h2 className="tw-mb-6 tw-text-xl tw-font-bold tw-text-white">{children}</h2>,
  h3: ({ children }: any) => <h3 className="tw-mb-4 tw-text-lg tw-font-bold tw-text-white">{children}</h3>,
  ul: ({ children }: any) => <ul className="tw-mb-6 tw-list-disc tw-pl-6">{children}</ul>,
  ol: ({ children }: any) => <ol className="tw-mb-6 tw-list-decimal tw-pl-6">{children}</ol>,
  li: ({ children }: any) => <li className="tw-leading-6">{children}</li>,
  strong: ({ children }: any) => <strong className="tw-font-bold">{children}</strong>,
  em: ({ children }: any) => <em className="tw-italic">{children}</em>,
  a: ({ children, href }: any) => (
    <a
      href={href}
      className="tw-text-white tw-underline hover:tw-text-gray-300"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  code: ({ children }: any) => (
    <code className="tw-rounded tw-bg-gray-800 tw-px-2 tw-py-1 tw-text-sm tw-text-white">{children}</code>
  ),
  blockquote: ({ children }: any) => (
    <blockquote className="tw-border-l-4 tw-border-gray-400 tw-pl-4 tw-italic tw-text-gray-300">{children}</blockquote>
  ),
  table: ({ children }: any) => (
    <div className="tw-mb-6 tw-overflow-x-auto">
      <table className="tw-w-full tw-border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }: any) => <thead>{children}</thead>,
  tbody: ({ children }: any) => <tbody>{children}</tbody>,
  tr: ({ children }: any) => <tr className="tw-border-b tw-border-[#0e2659]">{children}</tr>,
  th: ({ children }: any) => (
    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-base tw-font-normal tw-text-[#4987ff]">{children}</th>
  ),
  td: ({ children }: any) => <td className="tw-px-4 tw-py-3 tw-text-base tw-text-white">{children}</td>,
};

const variantComponents: Record<string, any> = {
  intro: {
    p: ({ children }: any) => (
      <p className="tw-mb-4 tw-text-left tw-text-lg tw-font-medium tw-leading-[26px] tw-text-white lg:tw-text-[22px] lg:tw-leading-[30px]">
        {children}
      </p>
    ),
    h1: ({ children }: any) => (
      <h1 className="tw-mb-4 tw-text-3xl tw-font-bold tw-text-white lg:tw-text-5xl">{children}</h1>
    ),
    h2: ({ children }: any) => (
      <h2 className="tw-mb-3 tw-text-2xl tw-font-bold tw-text-white lg:tw-text-3xl">{children}</h2>
    ),
    h3: ({ children }: any) => <h3 className="tw-mb-3 tw-text-xl tw-font-bold tw-text-white">{children}</h3>,
    ul: ({ children }: any) => (
      <ul className="tw-mb-6 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-pl-0">{children}</ul>
    ),
    ol: ({ children }: any) => (
      <ol className="tw-mb-6 tw-flex tw-list-decimal tw-flex-col tw-gap-2 tw-pl-6">{children}</ol>
    ),
    li: ({ children }: any) => (
      <li className="tw-flex tw-items-start tw-gap-4 tw-leading-6">
        <ListCheckmark className="tw-relative tw-mt-1 tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-white" />
        <span className="tw-flex-1 tw-text-left tw-text-lg tw-font-semibold tw-leading-6 tw-tracking-[-0.40px] tw-text-white lg:tw-text-xl">
          {children}
        </span>
      </li>
    ),
    strong: ({ children }: any) => <strong className="tw-font-semibold tw-text-[#6dfbff]">{children}</strong>,
  },
};

const resolveComponents = (variant?: string, overrides?: any) => {
  const variantMap = variant ? variantComponents[variant] || {} : {};
  return {
    ...baseComponents,
    ...variantMap,
    ...(overrides || {}),
  };
};

const MarkdownContent = ({ content, variant = 'default', componentsOverride, className }: MarkdownContentProps) => {
  if (!content) return null;

  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={resolveComponents(variant, componentsOverride)}>
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownContent;
